import { useNavigate, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ShieldAlert } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import { useCompanyStore } from '@/store/company-store'
import { Button } from '@/components/ui/button'

/**
 * Soft enforcement of the company "require 2FA" toggle: a persistent
 * banner on every page until the signed-in user enrolls a verified
 * TOTP factor. Hidden on /profile itself (that's where they fix it).
 */
export function TwoFactorBanner() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const { activeCompany } = useCompanyStore()

  const { data: required } = useQuery({
    queryKey: ['2fa-required', activeCompany?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('company_features')
        .select('two_factor_required')
        .eq('company_id', activeCompany!.id)
        .maybeSingle()
      return !!data?.two_factor_required
    },
    enabled: !!activeCompany?.id,
    staleTime: 5 * 60 * 1000,
  })

  const { data: enrolled } = useQuery({
    queryKey: ['mfa-enrolled', user?.id],
    queryFn: async () => {
      const { data } = await supabase.auth.mfa.listFactors()
      return (data?.totp ?? []).some(f => f.status === 'verified')
    },
    enabled: !!user && !!required,
    staleTime: 60 * 1000,
  })

  if (!required || enrolled !== false || location.pathname === '/profile') return null

  return (
    <div className="flex items-center justify-between gap-3 bg-amber-50 border-b border-amber-200 px-6 py-2.5">
      <p className="flex items-center gap-2 text-sm text-amber-800">
        <ShieldAlert className="h-4 w-4 shrink-0" />
        <span><strong>{activeCompany?.name}</strong> requires two-factor authentication — set it up now to keep your access secure.</span>
      </p>
      <Button size="sm" variant="outline" className="border-amber-300 text-amber-800 hover:bg-amber-100 shrink-0" onClick={() => navigate('/profile')}>
        Set Up 2FA
      </Button>
    </div>
  )
}
